/**
 * RightRail — Section 4 of /map redesign spec (founder approved 2026-05-05).
 *
 * Vertical 48px-wide rail attached to the right edge of the map canvas.
 * 8 buttons stacked top-to-bottom (Lucide icons), each owns a discrete
 * concern from the spec table:
 *
 *   1. Plus       — zoom in (map.zoomIn)
 *   2. Minus      — zoom out (map.zoomOut)
 *   3. Crosshair  — re-center on active scope (amber dot indicator when
 *                   viewport is far from scope bbox)
 *   4. Map        — basemap selector popover (3 options)
 *   5. Layers     — contour selector popover (reference / planting)
 *   6. Sprout     — crops popout (Layer 11 toggle + crop filter)
 *   7. Tag        — labels popover (4 sub-toggles 12a/b/c/d)
 *   8. Satellite  — toggles SAT STRIP at the bottom-center
 *
 * Popovers are local DOM (not Radix) — single open at a time, click-outside
 * to close. Keeps the rail stateless w.r.t. the rest of the app and avoids
 * pulling in an extra dependency for what is effectively a tooltip menu.
 */
import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import {
  Plus,
  Minus,
  Crosshair,
  Map as MapIcon,
  Layers,
  Sprout,
  Mountain,
  Tag,
  Satellite,
  Ruler,
  PencilRuler,
  MapPinPlus,
  Search,
} from 'lucide-react';
import { FieldSearchPanel } from '@/components/map/FieldSearchPanel';
import { useMapStore } from '@/lib/map/useMapStoreV2';
// AK-320: measure-инструмент живёт в отдельном лёгком сторе (НЕ mapReducer).
import { useMeasureStore } from '@/lib/map/measure/measureStore';
// AK-321: contour-editor — отдельный стор (НЕ mapReducer, НЕ measureStore).
import { useEditorStore } from '@/lib/map/editor/editorStore';
// AK-334: «+ Поле» tap-to-field — отдельный стор (НЕ mapReducer). Взаимно
// эксклюзивен с линейкой и редактором.
import { useAddFieldStore } from '@/lib/map/addField/addFieldStore';
import { useCan, PERM } from '@/lib/auth/usePermissions';
import { fetchScopeExtents as sharedScopeExtents } from '@/lib/http/sharedQueries';
import type {
  Basemap,
  ContourMode,
  LabelToggles,
  ActiveScope,
} from '@/lib/map/state/mapState';

interface RightRailProps {
  map: maplibregl.Map | null;
}

interface ScopeExtents {
  /**
   * EPSG:3857 [minx, miny, maxx, maxy]. `null` — в скоупе ещё нет полей.
   *
   * Именно `null`, а не отсутствие ответа: бэкенд с AK-110 v2 намеренно
   * отдаёт 200 с пустым bbox вместо 404 («frontend skips fitBounds»).
   * Здесь тип годами врал, что кортеж есть всегда, и первый же клиент на
   * свежем тенанте ловил белый экран с `null is not an object (evaluating
   * 'bbox[0]')` — карта падала на пустом хозяйстве.
   */
  bbox: [number, number, number, number] | null;
  hull_geojson: GeoJSON.Geometry | null;
  field_count: number;
  area_ha: number;
}

/**
 * Границы скоупа в lng/lat для `fitBounds`. `null` — показывать нечего:
 * полей нет либо bbox пришёл битым.
 *
 * Единственное место, где читается `bbox[i]`, — чтобы гард нельзя было
 * забыть в новом вызове.
 */
export function scopeBounds(
  extents: Pick<ScopeExtents, 'bbox'> | null,
): [[number, number], [number, number]] | null {
  const bbox = extents?.bbox;
  if (!bbox || bbox.length !== 4 || !bbox.every((n) => Number.isFinite(n))) {
    return null;
  }
  const [minLng, minLat] = mercatorToLngLat([bbox[0], bbox[1]]);
  const [maxLng, maxLat] = mercatorToLngLat([bbox[2], bbox[3]]);
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

type PopoverId = 'basemap' | 'contour' | 'crops' | 'labels' | null;

const BASEMAPS: Array<{ value: Basemap; label: string }> = [
  { value: 'satellite', label: 'Спутник' },
  { value: 'hybrid', label: 'Гибрид' },
  { value: 'streets', label: 'Карта' },
];

const CONTOURS: Array<{ value: ContourMode; label: string }> = [
  { value: 'planting', label: 'Посев' },
  { value: 'reference', label: 'Эталон' },
];

// AK-297 (design review 2026-06-27): «Номер поля» и «Площадь» слиты AK-299 в
// единую pill «номер · площадь» (один symbol-слой, predicate number || area),
// поэтому два раздельных тоггла вводили в заблуждение — оба показывали ту же
// pill с обоими полями. Матрица тоглов финализирована: один combined-тоггл
// «Номер · площадь» управляет pill'ом, плюс независимые «Название культуры» и
// «Иконка культуры». Combined-тоггл (`numberArea`) не хранится в state — он
// производный (number || area) и флипает оба ключа разом (см. onToggleLabel).
type LabelRow =
  | { kind: 'combined'; combinedKeys: Array<keyof LabelToggles>; label: string }
  | { kind: 'single'; key: keyof LabelToggles; label: string };

const LABEL_ROWS: LabelRow[] = [
  { kind: 'combined', combinedKeys: ['number', 'area'], label: 'Номер · площадь' },
  { kind: 'single', key: 'crop', label: 'Название культуры' },
  { kind: 'single', key: 'icon', label: 'Иконка культуры' },
];

/** EPSG:3857 → EPSG:4326 (mirror of RecenterButton math). */
function mercatorToLngLat([x, y]: [number, number]): [number, number] {
  const lng = (x / 20037508.34) * 180;
  let lat = (y / 20037508.34) * 180;
  lat = (180 / Math.PI) * (2 * Math.atan(Math.exp((lat * Math.PI) / 180)) - Math.PI / 2);
  return [lng, lat];
}

/**
 * Перф-аудит 14.08 (корень 6): раньше эту ручку дёргали независимо и панель, и
 * fitBounds карты, а 404 (у холдинга просто нет полей) уходил в ретраи — до 14
 * повторов за сессию. Теперь оба зовут общий кэш, где «нет данных» — это
 * кэшируемое значение `null`, а не ошибка.
 */
async function fetchScopeExtents(scope: ActiveScope): Promise<ScopeExtents | null> {
  const data = await sharedScopeExtents(scope.type, scope.id ?? null);
  return (data as ScopeExtents | null) ?? null;
}

function RailButton({
  onClick,
  label,
  testId,
  active,
  indicator,
  disabled,
  children,
}: {
  onClick: () => void;
  label: string;
  testId?: string;
  active?: boolean;
  indicator?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      data-testid={testId}
      data-active={active ? 'true' : undefined}
      className={
        'relative flex h-10 w-10 items-center justify-center rounded-md border transition-colors ' +
        (disabled
          ? 'bg-background text-muted-foreground border-input opacity-50 cursor-not-allowed'
          : active
            ? 'bg-primary text-primary-foreground border-primary'
            : 'bg-background text-foreground border-input hover:bg-accent')
      }
    >
      {children}
      {indicator && (
        <span
          aria-hidden
          data-testid={testId ? `${testId}-indicator` : undefined}
          // Amber off-scope dot per Section 4 spec re-center button states.
          // Токены вместо хардкода `#F4A300` + белого кольца: кольцо цвета
          // фона панели, а не всегда белое — в тёмной теме белая обводка
          // выглядела дефектом (design review 18.08, зона 1 · А6).
          className="absolute right-1 top-1 h-2 w-2 rounded-full bg-accent-brand ring-2 ring-background"
        />
      )}
    </button>
  );
}

function Popover({
  testId,
  children,
}: {
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <div
      data-testid={testId}
      role="dialog"
      // Anchored to the immediate left of the rail (rail is 48px + 16px right
      // offset; popover is 200px nominal — place it 56px to the left so it
      // aligns with the active button visually).
      style={{
        position: 'absolute',
        right: 56,
        top: 0,
        zIndex: 31,
      }}
      className="rounded-md border border-border bg-background/95 backdrop-blur-sm shadow-lg p-2 min-w-[180px]"
    >
      {children}
    </div>
  );
}

export function RightRail({ map }: RightRailProps) {
  // T11 (Sprint 2026-21): RightRail is V2-only — reads/writes against the
  // global useMapStoreV2 (singleton). Each selector is a single-field hook
  // so React skips re-renders when unrelated state slices change. Mirrors
  // the pattern used by SatelliteStrip and MapVerticalLegend after T9/T10.
  const basemap = useMapStore((s) => s.basemap);
  const contourMode = useMapStore((s) => s.contourMode);
  const cropFillOn = useMapStore((s) => s.cropFillOn);
  // AK-211: слой «Рельеф» — toggle заливки полей по уклону.
  const terrainOn = useMapStore((s) => s.terrainOn);
  const labels = useMapStore((s) => s.labels);
  const activeScope = useMapStore((s) => s.activeScope);
  const satelliteStripOpen = useMapStore((s) => s.satelliteStripOpen);
  const dispatch = useMapStore((s) => s.dispatch);
  // AK-320: measure-тул toggle (отдельный стор).
  const measureActive = useMeasureStore((s) => s.active);
  const activateMeasure = useMeasureStore((s) => s.activate);
  const deactivateMeasure = useMeasureStore((s) => s.deactivate);
  // AK-321: contour-editor toggle (отдельный стор). Взаимно эксклюзивен с
  // measure — включение редактора гасит линейку и наоборот.
  const editorActive = useEditorStore((s) => s.active);
  const activateEditor = useEditorStore((s) => s.activate);
  const deactivateEditor = useEditorStore((s) => s.deactivate);
  // AK-334: «+ Поле» tap-to-field toggle (отдельный стор). Взаимно эксклюзивен
  // с линейкой и редактором — включение гасит соседей.
  const addFieldActive = useAddFieldStore((s) => s.active);
  const activateAddField = useAddFieldStore((s) => s.activate);
  const deactivateAddField = useAddFieldStore((s) => s.deactivate);
  // AK-363: «+ Поле» — структурный write (backend require_structure_write →
  // ресурс `structure`). Нет права `structure:c` → кнопку рисования не
  // показываем (создание поля недоступно). Контур-редактор (edit) остаётся —
  // он также гейтится structure на бэке, но правит существующие контуры;
  // здесь скрываем именно создание нового поля.
  const canCreateField = useCan(PERM.fieldWrite, 'c');

  const [openPopover, setOpenPopover] = useState<PopoverId>(null);
  // AK-378: поиск поля по имени. Доступен только в farm-scope (предусловие);
  // farm_id = activeScope.id при type==='farm'. Панель монтируется по флагу.
  const [searchOpen, setSearchOpen] = useState(false);
  const searchAvailable = activeScope.type === 'farm' && !!activeScope.id;
  const [extents, setExtents] = useState<ScopeExtents | null>(null);
  const [offScope, setOffScope] = useState(false);
  const railRef = useRef<HTMLDivElement>(null);

  // AK-297: combined «Номер · площадь» toggle. Производное состояние =
  // (number || area); один клик переводит обе подписи в одно состояние:
  //   ON  (хоть одна включена) → выключить обе (флипаем включённые)
  //   OFF (обе выключены)      → включить обе (флипаем обе)
  // Реализуем поверх существующего TOGGLE_LABEL (флип per-key) без нового
  // экшена и без изменения формы state — registry-predicate (number || area)
  // и persist остаются как есть.
  const toggleCombined = (keys: Array<keyof LabelToggles>) => {
    const anyOn = keys.some((k) => labels[k]);
    // anyOn → выключаем только включённые; !anyOn → включаем обе.
    const keysToFlip = anyOn ? keys.filter((k) => labels[k]) : keys;
    for (const key of keysToFlip) {
      dispatch({ type: 'TOGGLE_LABEL', key });
    }
  };

  // AK-378: если scope ушёл из farm (сменили хозяйство/поднялись в кластер) —
  // закрываем панель поиска, чтобы не искать в невалидном scope.
  useEffect(() => {
    if (!searchAvailable && searchOpen) setSearchOpen(false);
  }, [searchAvailable, searchOpen]);

  // Click-outside closes any open popover.
  useEffect(() => {
    if (!openPopover) return;
    const handler = (e: MouseEvent) => {
      if (!railRef.current) return;
      if (!railRef.current.contains(e.target as Node)) {
        setOpenPopover(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openPopover]);

  // Fetch scope extents on scope change so we can re-center.
  useEffect(() => {
    let cancelled = false;
    setExtents(null);
    fetchScopeExtents(activeScope).then((data) => {
      if (!cancelled) setExtents(data);
    });
    return () => {
      cancelled = true;
    };
  }, [activeScope]);

  // Track viewport vs scope: amber indicator when off-scope.
  useEffect(() => {
    const bounds = scopeBounds(extents);
    if (!map || !bounds) {
      // Нет полей — не от чего отсчитывать «уехал ли вид»; индикатор гасим.
      setOffScope(false);
      return;
    }
    const check = () => {
      const center = map.getCenter();
      const [[minLng, minLat], [maxLng, maxLat]] = bounds;
      const cLng = (minLng + maxLng) / 2;
      const cLat = (minLat + maxLat) / 2;
      const w = maxLng - minLng;
      const h = maxLat - minLat;
      setOffScope(Math.abs(center.lng - cLng) > w * 0.75 || Math.abs(center.lat - cLat) > h * 0.75);
    };
    map.on('moveend', check);
    check();
    return () => {
      map.off('moveend', check);
    };
  }, [map, extents]);

  const togglePopover = (id: PopoverId) =>
    setOpenPopover((cur) => (cur === id ? null : id));

  const bounds = scopeBounds(extents);

  const recenter = () => {
    if (!map || !bounds) return;
    map.fitBounds(bounds, { padding: 80, duration: 600 });
  };

  return (
    <div
      ref={railRef}
      data-testid="map-right-rail"
      // 48px wide track, top-right of canvas. z=30 to sit above MapLibre's
      // own controls layer if any.
      style={{
        position: 'absolute',
        top: 16,
        right: 16,
        zIndex: 30,
        width: 48,
      }}
      className="flex flex-col gap-2"
    >
      {/*
        AK-378 «Поиск поля по имени»: лупа открывает панель поиска (Input +
        список совпадений). Доступна только в farm-scope — вне хозяйства
        кнопка disabled с подсказкой (предусловие founder). Клик по результату
        → flyTo центроид + жёлтый highlight выбранного поля.
      */}
      <div style={{ position: 'relative' }}>
        <RailButton
          onClick={() => {
            if (!searchAvailable) return;
            setSearchOpen((v) => !v);
            setOpenPopover(null);
          }}
          label={searchAvailable ? 'Поиск поля' : 'Сначала выберите хозяйство'}
          testId="rail-search"
          active={searchOpen}
          disabled={!searchAvailable}
        >
          <Search size={18} />
        </RailButton>
        {searchOpen && searchAvailable && activeScope.id && (
          <FieldSearchPanel
            map={map}
            farmId={activeScope.id}
            onClose={() => setSearchOpen(false)}
          />
        )}
      </div>

      <RailButton
        onClick={() => map?.zoomIn()}
        label="Приблизить"
        testId="rail-zoom-in"
      >
        <Plus size={18} />
      </RailButton>

      <RailButton
        onClick={() => map?.zoomOut()}
        label="Отдалить"
        testId="rail-zoom-out"
      >
        <Minus size={18} />
      </RailButton>

      <RailButton
        onClick={recenter}
        /* Пока полей нет, возвращаться некуда: кнопка неактивна и говорит
           почему, а не делает вид, что сработала. */
        label={bounds ? 'Вернуться к полям' : 'Полей пока нет — добавьте первое'}
        testId="rail-recenter"
        indicator={offScope}
        disabled={!bounds}
      >
        <Crosshair size={18} />
      </RailButton>

      <div style={{ position: 'relative' }}>
        <RailButton
          onClick={() => togglePopover('basemap')}
          label="Подложка"
          testId="rail-basemap"
          active={openPopover === 'basemap'}
        >
          <MapIcon size={18} />
        </RailButton>
        {openPopover === 'basemap' && (
          <Popover testId="rail-basemap-popover">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Подложка
            </div>
            <div className="flex flex-col gap-1">
              {BASEMAPS.map((b) => (
                <button
                  key={b.value}
                  type="button"
                  data-testid={`rail-basemap-${b.value}`}
                  onClick={() => {
                    dispatch({ type: 'SET_BASEMAP', basemap: b.value });
                    setOpenPopover(null);
                  }}
                  className={
                    'text-left text-sm px-2 py-1 rounded ' +
                    (basemap === b.value
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-accent')
                  }
                >
                  {b.label}
                </button>
              ))}
            </div>
          </Popover>
        )}
      </div>

      <div style={{ position: 'relative' }}>
        <RailButton
          onClick={() => togglePopover('contour')}
          label="Контуры"
          testId="rail-contour"
          active={openPopover === 'contour'}
        >
          <Layers size={18} />
        </RailButton>
        {openPopover === 'contour' && (
          <Popover testId="rail-contour-popover">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Контуры
            </div>
            <div className="flex flex-col gap-1">
              {CONTOURS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  data-testid={`rail-contour-${c.value}`}
                  onClick={() => {
                    dispatch({ type: 'SET_CONTOUR_MODE', mode: c.value });
                    setOpenPopover(null);
                  }}
                  className={
                    'text-left text-sm px-2 py-1 rounded ' +
                    (contourMode === c.value
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-accent')
                  }
                >
                  {c.label}
                </button>
              ))}
            </div>
          </Popover>
        )}
      </div>

      <div style={{ position: 'relative' }}>
        <RailButton
          onClick={() => togglePopover('crops')}
          label="Культуры"
          testId="rail-crops"
          active={openPopover === 'crops'}
        >
          <Sprout size={18} />
        </RailButton>
        {openPopover === 'crops' && (
          // 320×400 popout per spec — fixed dims to leave room for the crop
          // list. Full CropFilterPanel reuse comes in a follow-up; for this
          // PR the popout exposes the Layer 11 toggle + a placeholder slot
          // that domain-team Crops will fill with the scope-aware crop
          // filter (existing CropFilterPanel ties to the legacy useMapStore
          // and not to the V2 store yet).
          <div
            data-testid="rail-crops-popover"
            role="dialog"
            style={{
              position: 'absolute',
              right: 56,
              top: 0,
              width: 320,
              maxHeight: 400,
              zIndex: 31,
            }}
            className="rounded-md border border-border bg-background/95 backdrop-blur-sm shadow-lg p-3 overflow-y-auto"
          >
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Заливка по культуре
            </div>
            <label className="flex items-center justify-between text-sm cursor-pointer mb-3">
              <span>Включить заливку</span>
              <input
                type="checkbox"
                data-testid="rail-crops-fill-toggle"
                checked={cropFillOn}
                onChange={() => dispatch({ type: 'TOGGLE_CROP_FILL' })}
                className="h-4 w-4"
              />
            </label>
            <div className="text-xs text-muted-foreground">
              {/* Placeholder — full CropFilterPanel V2 integration на
                  следующем шаге (Section 4.5 follow-up). */}
              Список культур со scope-aware площадями появится здесь.
            </div>
          </div>
        )}
      </div>

      {/*
        AK-211 «Рельеф» (эпик AK-198): choropleth-заливка полей по классу
        уклона (slope_mean из MVT). Простой toggle — рядом с «Культуры», как в
        утв. мокапе (вариант 1). Работает в любом scope (вектор, без farm-lock
        спутников). Взаимно эксклюзивен с заливкой по культуре / raster на
        уровне visibility-предикатов слоёв. Легенда — MapVerticalLegend.
      */}
      <RailButton
        onClick={() => dispatch({ type: 'TOGGLE_TERRAIN' })}
        label="Рельеф"
        testId="rail-terrain"
        active={terrainOn}
      >
        <Mountain size={18} />
      </RailButton>

      <div style={{ position: 'relative' }}>
        <RailButton
          onClick={() => togglePopover('labels')}
          label="Подписи"
          testId="rail-labels"
          active={openPopover === 'labels'}
        >
          <Tag size={18} />
        </RailButton>
        {openPopover === 'labels' && (
          <Popover testId="rail-labels-popover">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Подписи
            </div>
            <div className="flex flex-col gap-1">
              {LABEL_ROWS.map((row) => {
                if (row.kind === 'combined') {
                  const checked = row.combinedKeys.some((k) => labels[k]);
                  return (
                    <label
                      key="number-area"
                      className="flex items-center justify-between gap-2 text-sm cursor-pointer px-1 py-1 rounded hover:bg-accent"
                    >
                      <span>{row.label}</span>
                      <input
                        type="checkbox"
                        data-testid="rail-label-number-area"
                        checked={checked}
                        onChange={() => toggleCombined(row.combinedKeys)}
                        className="h-4 w-4"
                      />
                    </label>
                  );
                }
                return (
                  <label
                    key={row.key}
                    className="flex items-center justify-between gap-2 text-sm cursor-pointer px-1 py-1 rounded hover:bg-accent"
                  >
                    <span>{row.label}</span>
                    <input
                      type="checkbox"
                      data-testid={`rail-label-${row.key}`}
                      checked={labels[row.key]}
                      onChange={() => dispatch({ type: 'TOGGLE_LABEL', key: row.key })}
                      className="h-4 w-4"
                    />
                  </label>
                );
              })}
            </div>
          </Popover>
        )}
      </div>

      {/*
        AK-334 «+ Поле» (tap-to-field): тап по карте → детект границ поля →
        черновик → форма сохранения. Взаимно эксклюзивен с линейкой и
        редактором. AddFieldPanel монтируется в v2.tsx по addFieldActive.
      */}
      {canCreateField && (
        <RailButton
          onClick={() => {
            if (addFieldActive) {
              deactivateAddField();
            } else {
              if (measureActive) deactivateMeasure();
              if (editorActive) deactivateEditor();
              activateAddField();
            }
          }}
          label="+ Поле"
          testId="rail-add-field"
          active={addFieldActive}
        >
          <MapPinPlus size={18} />
        </RailButton>
      )}

      {/*
        AK-320 «Линейка на карте»: измерение расстояний/площадей. Доступно
        в любом scope (геометрия не зависит от хозяйства). Активная кнопка =
        measureActive; клик toggle'ит отдельный measureStore, MeasurePanel
        монтируется в v2.tsx по measureActive.
      */}
      <RailButton
        onClick={() => {
          if (measureActive) {
            deactivateMeasure();
          } else {
            // Эксклюзив: гасим редактор контура и «+ Поле» перед линейкой.
            if (editorActive) deactivateEditor();
            if (addFieldActive) deactivateAddField();
            activateMeasure('line');
          }
        }}
        label="Линейка"
        testId="rail-measure"
        active={measureActive}
      >
        <Ruler size={18} />
      </RailButton>

      {/*
        AK-321 «Редактор контура — обвести поле»: terra-draw select-mode.
        Клик по полю при активном редакторе грузит его контур как editable.
        Взаимно эксклюзивен с линейкой (см. onClick). Панель монтируется в
        v2.tsx по editorActive.
      */}
      <RailButton
        onClick={() => {
          if (editorActive) {
            deactivateEditor();
          } else {
            if (measureActive) deactivateMeasure();
            if (addFieldActive) deactivateAddField();
            activateEditor();
          }
        }}
        label="Обвести поле"
        testId="rail-contour-editor"
        active={editorActive}
      >
        <PencilRuler size={18} />
      </RailButton>

      {/*
        AK-106 Task 2 (founder lock-in 2026-05-08): satellite products work
        only при выборе хозяйства (farm scope). Скрываем satellite trigger
        для company/cluster/holding/brigade/array/field/tenant — пользователь
        не должен иметь возможность открыть SatelliteStrip без farm scope.
        Field labels (icons, numbers, area, crop) STAY visible — только
        satellite stuff скрывается. Reducer SET_ACTIVE_SCOPE flips
        satelliteStripOpen→false при переходе в non-farm scope (mute-all).
      */}
      {activeScope.type === 'farm' && (
        <RailButton
          // T11 (Sprint 2026-21): explicit OPEN/CLOSE_SAT_PANEL — replaces the
          // legacy TOGGLE_SATELLITE_STRIP. OPEN seeds activeProduct (NDVI is
          // the canonical default per spec §8 — user can pick another product
          // pill once the strip is visible). CLOSE is the founder lock-in #2
          // mute-all (clears product/raster/RGB/cloud/date in one transition).
          onClick={() =>
            satelliteStripOpen
              ? dispatch({ type: 'CLOSE_SAT_PANEL' })
              : dispatch({ type: 'OPEN_SAT_PANEL', product: 'NDVI' })
          }
          label="Снимки"
          testId="rail-satellite"
          active={satelliteStripOpen}
        >
          <Satellite size={18} />
        </RailButton>
      )}
    </div>
  );
}
